import { format, getISODay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { addDays, differenceInSeconds, getDateFnsLocale } from './date'
import { legacyParse } from './legacyDateParser'

type DateInput = Date | string

const AMSTERDAM_TIMEZONE = 'Europe/Amsterdam'

function getLocalizedDate(date: DateInput): Date {
    return toZonedTime(legacyParse(date), AMSTERDAM_TIMEZONE)
}

function formatLocalized(date: DateInput, formatString: string): string {
    return format(getLocalizedDate(date), formatString, { locale: getDateFnsLocale() })
}

/**
 * Convert a datetime to a time like 18:00.
 * @param date
 * @returns {string}
 */
export function convertDateToTime(date: DateInput) {
    return formatLocalized(date, 'HH:mm')
}

/**
 * Convert a date to a day string like 'Monday'.
 * @param date
 * @returns {string}
 */
export function convertDateToDay(date: DateInput) {
    return formatLocalized(date, 'EEEE')
}

/**
 * Convert a date to a date string like (21 July).
 * @param date
 * @returns {string}
 */
export function convertDateToDateString(date: DateInput) {
    return formatLocalized(date, 'd MMMM')
}

/**
 * Convert a date to a short date string like (21 aug).
 * @param date
 * @returns {string}
 */
export function convertDateToShortDateString(date: DateInput) {
    return formatLocalized(date, 'd MMM')
}

/**
 * Convert a date to a date string like (21 Mar. 2019).
 * @param date
 * @returns {string}
 */
export function convertDateToDateYearString(date: DateInput) {
    return formatLocalized(date, 'd MMM yyyy')
}

/**
 * Convert a date to a date string like (21 March 2019).
 * @param date
 * @returns {string}
 */
export function convertDateToDateYearCompleteString(date: DateInput) {
    return formatLocalized(date, 'd MMMM yyyy')
}

/**
 * Convert a date to a date time string like (21 Aug 2019 10:00:00).
 * @param date
 * @returns {string}
 */
export function convertDateToDateTimeString(date: DateInput) {
    return formatLocalized(date, 'd MMM yyyy HH:mm')
}

/**
 * Convert a date to a month day like (25).
 * @param date
 * @returns {string}
 */
export function convertDateToMonthDay(date: DateInput) {
    return formatLocalized(date, 'd')
}

/**
 * Convert a date to a month day like (25).
 * @param date
 * @returns {string}
 */
export function convertDateToMonth(date: DateInput) {
    return formatLocalized(date, 'MMMM')
}

/**
 * Converts a date to the Year-Month-Day format.
 *
 * @param date
 * @param newFormat
 * @returns {string}
 */
export function formatDate(date: DateInput, newFormat = 'yyyy-MM-dd'): string {
    return `${formatLocalized(date, newFormat)}`
}

const prettifySegment = (unit: number): string => (`${unit}`.length > 1 ? `${unit}` : `0${unit}`)

export const getTimeSegments = (startTime: Date, endTime: Date = new Date()): { h: string, m: string, s: string } => {
    const totalSeconds = differenceInSeconds(endTime, startTime)

    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = Math.floor((totalSeconds % 3600) % 60)

    return {
        h: prettifySegment(h),
        m: prettifySegment(m),
        s: prettifySegment(s),
    }
}

export function getWeekDays(weekStart: Date): Date[] {
    const days = [weekStart]
    for (let i = 0; i < 7; i += 1) {
        days.push(addDays(weekStart, i))
    }
    return days
}

export function getWeekRange(date: Date) {
    const isoDay = getISODay(date)

    return {
        from: addDays(date, 1 - isoDay),
        to: addDays(date, 7 - isoDay),
    }
}

export const getDifferenceInTime = (startTime: Date, endTime: Date = new Date(), withMinutes = false): string => {
    const segments = getTimeSegments(startTime, endTime)

    return `${segments.h}:${segments.m}${withMinutes ? `:${segments.s}` : ''}`
}
